import React, { useRef, useState } from 'react';
import { alpha, useTheme } from '@mui/material';
import styled, { css } from 'styled-components';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import { Button } from '../../../components/library/Button';
import Loader from '../../../components/library/Loader';

export interface CreateTitleAction {
	key: string;
	label: string;
	onClick: () => void;
}

interface CreateTitleSplitButtonProps {
	/** Ordered actions; `index` maps to `selectedIndex`. The selected action is the primary (main) button. */
	options: CreateTitleAction[];
	selectedIndex: number;
	/** Fired when the user picks a different action from the dropdown (used to persist the preference). */
	onSelectedIndexChange: (index: number) => void;
	disabled?: boolean;
	loading?: boolean;
}

// Occupies the same slot as the old single primary button: grows to fill its half of the action bar.
const Wrapper = styled.div(
	() => css`
		display: flex;
		flex-grow: 1;
	`,
);

// The two segments share the look of the in-house `primary-blue` button but join into one control:
// square the inner corners so only the outer edges stay rounded.
const MainButton = styled(Button)(
	() => css`
		&&& {
			border-bottom-right-radius: 0;
			border-top-right-radius: 0;
			flex-grow: 1;
		}
	`,
);

const ArrowButton = styled(Button)(
	({ theme }) => css`
		&&& {
			border-bottom-left-radius: 0;
			border-left: 1px solid ${alpha(theme.palette.text.reverseMain, 0.3)};
			border-top-left-radius: 0;
			flex-grow: 0;
			min-width: 44px;
			padding-left: 0;
			padding-right: 0;
			width: 44px;
		}
	`,
);

// Match the width of the "Create Single / Create Multiple / Import MEC" dropdown menu, which sizes
// to its longest item (~200px). The split button's own labels are short, so pin the width to align.
const StyledMenuList = styled(MenuList)(
	() => css`
		&&& {
			width: 200px;
		}
	`,
);

/**
 * Split button for the create-title action bar. Visually matches the in-house `primary-blue` button
 * (same colours, radius and full-height sizing) so it sits alongside the "Back" button like the old
 * single-button design, with an added dropdown segment.
 *
 * The main button performs the currently selected action; the dropdown lets the user switch which
 * action is the default. Selecting a menu item only changes the default (the caller persists it) —
 * it does not fire the action, matching the canonical MUI split-button pattern.
 */
export function CreateTitleSplitButton({
	options,
	selectedIndex,
	onSelectedIndexChange,
	disabled = false,
	loading = false,
}: CreateTitleSplitButtonProps) {
	const theme = useTheme();
	const [open, setOpen] = useState(false);
	const anchorRef = useRef<HTMLDivElement>(null);

	const selected = options[selectedIndex] ?? options[0];

	const handleMenuItemClick = (index: number) => {
		onSelectedIndexChange(index);
		setOpen(false);
	};

	return (
		<Wrapper ref={anchorRef}>
			<MainButton nxstyle="primary-blue" $fullHeight disabled={disabled || loading} onClick={() => selected.onClick()}>
				{loading ? <Loader size={20} color={theme.palette.light.main} /> : selected.label}
			</MainButton>
			<ArrowButton
				nxstyle="primary-blue"
				$fullHeight
				aria-label="Select create title action"
				aria-haspopup="menu"
				disabled={disabled || loading}
				onClick={() => setOpen((prev) => !prev)}
			>
				<ArrowDropDownIcon />
			</ArrowButton>
			<Popper
				open={open}
				anchorEl={anchorRef.current}
				transition
				disablePortal
				placement="top-end"
				style={{ zIndex: theme.zIndex.modal + 1 }}
			>
				{({ TransitionProps: transitionProps }) => (
					<Grow {...transitionProps}>
						<Paper>
							<ClickAwayListener onClickAway={() => setOpen(false)}>
								<StyledMenuList autoFocusItem>
									{options.map((option, index) => (
										<MenuItem
											key={option.key}
											selected={index === selectedIndex}
											onClick={() => handleMenuItemClick(index)}
										>
											{option.label}
										</MenuItem>
									))}
								</StyledMenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</Wrapper>
	);
}
